import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { createFilterOptions } from "@mui/material/Autocomplete";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import TextField from "../../../components/Form/TextField";
import AutocompleteField from "../../../components/Form/AutocompleteField";
import { useForm } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import { startSendEmailFromOwner } from "../../../actions/owner";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import {
  BUTTON_CANCEL,
  FIELD_REQUIRED,
  TABLE_ADMIN_SEND_EMAIL_BODY_LABEL,
  TABLE_ADMIN_SEND_EMAIL_MODAL_TITLE,
  TABLE_ADMIN_SEND_EMAIL_RECIPIENTS_LABEL,
  TABLE_ADMIN_SEND_EMAIL_RECIPIENTS_PLACEHOLDER,
  TABLE_ADMIN_SEND_EMAIL_SELECT_ALL,
  TABLE_ADMIN_SEND_EMAIL_SEND_BUTTON,
  TABLE_ADMIN_SEND_EMAIL_SUBJECT_LABEL,
  TABLE_ADMIN_SEND_EMAIL_SUCCESS,
} from "../../../i18n/keysTranslations";
import { ALERT_ICON_TYPE_SUCCESS } from "../../../utils/constants";
import { optionsAdsRoles } from "../../../utils/options";
import { sortUsersByRolePriority } from "../utils/sortUsersByRolePriority";

const SELECT_ALL_VALUE = "__SELECT_ALL__";

const filterUserOptions = createFilterOptions({
  stringify: (option) =>
    `${option.label || ""} ${option.email || ""} ${option.role || ""}`,
});

const ModalSendClientEmail = ({
  modalOpen,
  onCloseModal,
  users = [],
  contextTitle = "",
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      subject: "",
      body: "",
      recipientEmails: [],
    },
  });

  const userOptions = useMemo(() => {
    const sorted = sortUsersByRolePriority(users || []);
    return sorted
      .filter((u) => u?.email && typeof u.email === "string")
      .map((u) => {
        const email = u.email.trim();
        const name = (u.name || "").trim() || email;
        return {
          value: email,
          label: `${name} (${email})`,
          role: u.role,
          email,
        };
      });
  }, [users]);

  const selectAllOption = useMemo(
    () => ({
      value: SELECT_ALL_VALUE,
      isSelectAll: true,
      label: t(TABLE_ADMIN_SEND_EMAIL_SELECT_ALL),
    }),
    [t]
  );

  const autocompleteOptions = useMemo(
    () => [selectAllOption, ...userOptions],
    [selectAllOption, userOptions]
  );

  const getRoleLabel = (role) => {
    const opt = optionsAdsRoles.find((o) => o.value === role);
    return opt ? t(opt.label) : role || "";
  };

  useEffect(() => {
    if (!modalOpen) {
      reset({
        subject: "",
        body: "",
        recipientEmails: [],
      });
    }
  }, [modalOpen, reset]);

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(
        startSendEmailFromOwner({
          emails: values.recipientEmails,
          subject: values.subject,
          body: values.body,
        })
      );

      if (result?.ok) {
        await SimpleAlert({
          title: t(TABLE_ADMIN_SEND_EMAIL_SUCCESS),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
        reset();
        onCloseModal();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle = contextTitle
    ? `${t(TABLE_ADMIN_SEND_EMAIL_MODAL_TITLE)} — ${contextTitle}`
    : t(TABLE_ADMIN_SEND_EMAIL_MODAL_TITLE);

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={modalTitle}
      maxWidth="sm"
      fullWidth
      showCloseButton={true}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
        <AutocompleteField
          control={control}
          name="recipientEmails"
          label={t(TABLE_ADMIN_SEND_EMAIL_RECIPIENTS_LABEL)}
          placeholder={t(TABLE_ADMIN_SEND_EMAIL_RECIPIENTS_PLACEHOLDER)}
          options={autocompleteOptions}
          multiple
          disableCloseOnSelect
          doTranslate={false}
          rules={{
            validate: (v) =>
              (Array.isArray(v) && v.length > 0) || t(FIELD_REQUIRED),
          }}
          errors={errors}
          filterOptions={(opts, state) => {
            const head = opts[0]?.isSelectAll ? opts[0] : null;
            const tail = head ? opts.slice(1) : opts;
            const filteredTail = filterUserOptions(tail, state);
            return head ? [head, ...filteredTail] : filteredTail;
          }}
          renderOption={(props, option) => {
            if (option.isSelectAll) {
              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    fontWeight: 600,
                  }}
                >
                  <Typography variant="body2">{option.label}</Typography>
                </Box>
              );
            }
            return (
              <Box
                component="li"
                {...props}
                sx={{
                  flexDirection: "column",
                  alignItems: "flex-start !important",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {getRoleLabel(option.role)}
                </Typography>
                <Typography variant="body2">{option.email}</Typography>
              </Box>
            );
          }}
        />

        <TextField
          fullWidth
          label={t(TABLE_ADMIN_SEND_EMAIL_SUBJECT_LABEL)}
          name="subject"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />

        <TextField
          fullWidth
          label={t(TABLE_ADMIN_SEND_EMAIL_BODY_LABEL)}
          name="body"
          variant="filled"
          color="primary"
          multiline
          minRows={5}
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />

        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: TABLE_ADMIN_SEND_EMAIL_SEND_BUTTON,
            loading: isSubmitting,
            type: "submit",
            onClick: () => {},
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalSendClientEmail;
